import { useEffect, useState } from 'react';

// Taken from https://usehooks.com/useScript/

export const USE_SCRIPT_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
};

export default function useScript(src, options = {}) {
  // Keep track of script status ("idle", "loading", "ready", "error")
  const [status, setStatus] = useState(
    src ? USE_SCRIPT_STATUS.LOADING : USE_SCRIPT_STATUS.IDLE
  );

  useEffect(
    () => {
      // Allow falsy src value if waiting on other data needed for
      // constructing the script URL passed to this hook.
      if (!src) {
        setStatus(USE_SCRIPT_STATUS.IDLE);

        return;
      }

      // Fetch existing script element by src
      // It may have been added by another intance of this hook
      let script = document.querySelector(`script[src="${src}"]`);

      if (!script) {
        // Create script
        script = document.createElement('script');
        script.src = src;
        script.setAttribute('data-status', USE_SCRIPT_STATUS.LOADING);
        Object.keys(options).forEach(key => {
          script.setAttribute(key, options[key]);
        });

        // Add script to document body
        document.body.appendChild(script);

        // Store status in attribute on script
        // This can be read by other instances of this hook
        const setAttributeFromEvent = event => {
          script.setAttribute(
            'data-status',
            event.type === 'load'
              ? USE_SCRIPT_STATUS.READY
              : USE_SCRIPT_STATUS.ERROR
          );
        };

        script.addEventListener('load', setAttributeFromEvent);
        script.addEventListener('error', setAttributeFromEvent);
      } else {
        // Grab existing script status from attribute and set to state.
        setStatus(script.getAttribute('data-status'));
      }

      // Script event handler to update status in state
      // Note: Even if the script already exists we still need to add
      // event handlers to update the state for *this* hook instance.
      const setStateFromEvent = event => {
        setStatus(
          event.type === 'load'
            ? USE_SCRIPT_STATUS.READY
            : USE_SCRIPT_STATUS.ERROR
        );
      };

      // Add event listeners
      script.addEventListener('load', setStateFromEvent);
      script.addEventListener('error', setStateFromEvent);

      // Remove event listeners on cleanup
      return () => {
        if (script) {
          script.removeEventListener('load', setStateFromEvent);
          script.removeEventListener('error', setStateFromEvent);
        }
      };
    },
    [src, options] // Only re-run effect if script src changes
  );

  return status;
}
